import * as Sentry from '@sentry/nextjs';

import { APIRequestLabel } from '../consts';
import { callHttpDelete, callHttpGet, callHttpPatch, callHttpPost } from '../http';
import {
  ActivationParentType,
  ActivationPhraseTemplateModel,
  ActivationPhraseTemplateType,
  ActivationRuleItemType,
  ActivationRuleModel,
  CreateOrUpdateActivationRuleModel
} from '../models/activations';

const getActivationPhraseTemplates = (get, set) => {
  return async (params?: {
    parentType: ActivationParentType;
    type?: ActivationPhraseTemplateType;
  }): Promise<ActivationPhraseTemplateModel[]> => {
    let qParams = { parentType: params.parentType };
    if (params.type) {
      params['type'] = params.type;
    }

    let q = new URLSearchParams(qParams).toString();

    await callHttpGet(
      get,
      set,
      APIRequestLabel.GET_ACTIVATION_PHRASE_TEMPLATES,
      `/activations/phrases/templates?${q}`
    );
    let apiCallStatus = get().apiCallStatus[APIRequestLabel.GET_ACTIVATION_PHRASE_TEMPLATES];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    return apiCallStatus.response.templates;
  };
};

const cleanupActivationRule = (rule: ActivationRuleModel): CreateOrUpdateActivationRuleModel => {
  return {
    name: rule.name,
    parentType: rule.parentType,
    items: rule.items.map((item) => {
      if (item.type === ActivationRuleItemType.PHRASE) {
        return {
          type: item.type,
          phrase: {
            templateEntityId: item.phrase.templateEntityId,
            args: item.phrase.args
          },
          operator: null
        };
      } else {
        return {
          type: item.type,
          phrase: null,
          operator: item.operator
        };
      }
    })
  };
};

const createActivationRule = (get, set) => {
  return async (rule: ActivationRuleModel): Promise<{ entityId: string }> => {
    await callHttpPost(
      get,
      set,
      APIRequestLabel.CREATE_ACTIVATION_RULE,
      `/activations/rules`,
      cleanupActivationRule(rule)
    );

    let apiCallStatus = get().apiCallStatus[APIRequestLabel.CREATE_ACTIVATION_RULE];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    return apiCallStatus.response;
  };
};

const getActivationRule = (get, set) => {
  return async (entityId: string): Promise<ActivationRuleModel> => {
    await callHttpGet(
      get,
      set,
      APIRequestLabel.GET_ACTIVATION_RULE,
      `/activations/rules/${entityId}`
    );
    let apiCallStatus = get().apiCallStatus[APIRequestLabel.GET_ACTIVATION_RULE];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    return apiCallStatus.response;
  };
};

const updateActivationRule = (get, set) => {
  return async (entityId: string, rule: ActivationRuleModel): Promise<void> => {
    await callHttpPatch(
      get,
      set,
      APIRequestLabel.UPDATE_ACTIVATION_RULE,
      `/activations/rules/${entityId}`,
      cleanupActivationRule(rule)
    );
  };
};

const deleteActivationRule = (get, set) => {
  return async (entityId: string): Promise<void> => {
    await callHttpDelete(
      get,
      set,
      APIRequestLabel.DELETE_ACTIVATION_RULE,
      `/activations/rules/${entityId}`
    );
  };
};

export {
  createActivationRule,
  deleteActivationRule,
  getActivationPhraseTemplates,
  getActivationRule,
  updateActivationRule
};
